import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider,
  TextField,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Link as LinkIcon,
  ContentCopy as CopyIcon,
  Send as SendIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../utils/api';
import { formatCalendarDate } from '../utils/dateUtils';

interface InvoiceLinkTarget {
  id: string;
  invoiceNumber: string;
  amount: number;
  taxAmount?: number;
  status: string;
  dueDate: string;
  horse: {
    name: string;
    /** Both resolved SERVER-side through billing/bill-to.ts — the payer on a third-party invoice. */
    ownerName?: string;
    ownerEmail?: string;
    /** The patient's ownership list. NOT the bill-to — last resort only. */
    owners?: Array<{ name?: string; email?: string }>;
  };
  client?: {
    id?: string;
    name?: string;
    email?: string;
  };
  /** One-time payer snapshot stored on the invoice instead of a clientId (INV-13). */
  thirdPartyPayer?: {
    name?: string;
    email?: string;
    phone?: string;
    address?: string;
  } | null;
  paymentLinkUrl?: string;
}

/** A client invoice: billed to its client, no patient or due date, always the whole balance. */
interface StatementLinkTarget {
  id: string;
  invoiceNumber: string;
  clientName?: string;
  clientEmail?: string;
  /** Shown until the link exists; then the amount the server made it for. */
  amountDue: number;
}

type SendPaymentLinkDialogProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  balanceDue?: number;
} & (
  | { invoice: InvoiceLinkTarget; statement?: undefined }
  | { statement: StatementLinkTarget; invoice?: undefined }
);

type DialogStep = 'initial' | 'generating' | 'ready' | 'sending' | 'sent' | 'error';

const SendPaymentLinkDialog: React.FC<SendPaymentLinkDialogProps> = ({
  open,
  onClose,
  onSuccess,
  invoice: invoiceTarget,
  statement,
  balanceDue,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  // Read only on the invoice branches: exactly one of the two targets is given.
  const invoice = invoiceTarget as InvoiceLinkTarget;
  const routeBase = statement ? `/billing/client-invoices/${statement.id}` : `/billing/${invoice.id}`;

  /*
   * B1: this dialog mails a LIVE pay link, so the recipient is the BILL-TO, not the
   * patient's owner. Same precedence as the backend seam (billing/bill-to.ts) and as
   * InvoiceDetail.tsx's own chain: the direct client, then the third-party payer
   * snapshot, then the server-resolved horse.owner* fields, and only then the owners
   * JSON. The payer leg is needed on BOTH halves — `owners[0]` is the patient's
   * ownership list, which the server deliberately never rewrites, so the name beside
   * the email would otherwise read "Jane Rider" over the insurer's address.
   *
   * ⚠ RULING B-1: the payer is TERMINAL, not one `||` in a chain. A snapshot carrying
   * no email is a NORMAL row — `normalizeThirdPartyPayer` requires only a name and this
   * app's own form labels the field "Payer Email (optional)" — and as a chain link it
   * fell through to `owners[0].email`, pre-filling the horse OWNER's address under the
   * insurer's name and mailing her a payable link for a bill she does not owe. The
   * backend refuses the same input rather than re-addressing it
   * (billing.controller.ts `sendPaymentLinkEmail`, and bill-to.ts for every read), so
   * once a payer is present this resolves to the payer's own values or to nothing, and
   * an empty value leaves the send disabled until a human types the payer's address.
   */
  const resolveEmail = (): string => {
    if (statement) return statement.clientEmail || '';
    if (invoice.client?.email) return invoice.client.email;
    if (invoice.thirdPartyPayer) return invoice.thirdPartyPayer.email || '';
    return invoice.horse.ownerEmail
      || (Array.isArray(invoice.horse.owners) && invoice.horse.owners.length > 0
        ? invoice.horse.owners[0]?.email || ''
        : '');
  };

  const resolveRecipientName = (): string => {
    if (statement) return statement.clientName || 'Client';
    if (invoice.client?.name) return invoice.client.name;
    // Same terminal rule, and the same label the backend uses for a snapshot with no
    // usable name (`UNNAMED_THIRD_PARTY_NAME`, billing/bill-to.ts) — a REST invoice
    // already answers it through `horse.ownerName`, while an offline replica row holds
    // the raw snapshot and would otherwise land on the patient's first owner.
    if (invoice.thirdPartyPayer) return invoice.thirdPartyPayer.name || 'Third-party payer';
    return invoice.horse.ownerName
      || (Array.isArray(invoice.horse.owners) && invoice.horse.owners.length > 0
        ? invoice.horse.owners[0]?.name || 'Client'
        : 'Client');
  };

  const [step, setStep] = useState<DialogStep>('initial');
  const [recipientEmail, setRecipientEmail] = useState(resolveEmail());
  const [editingEmail, setEditingEmail] = useState(false);
  const [paymentLinkUrl, setPaymentLinkUrl] = useState<string | null>(
    statement ? null : invoice.paymentLinkUrl || null,
  );
  const [linkAmount, setLinkAmount] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) {
      setRecipientEmail(resolveEmail());
      setEditingEmail(false);
      setErrorMessage('');
      setCopied(false);
      setLinkAmount(null);

      if (!statement && invoice.paymentLinkUrl && balanceDue == null) {
        setPaymentLinkUrl(invoice.paymentLinkUrl);
        setStep('ready');
      } else {
        setPaymentLinkUrl(null);
        setStep('initial');
      }
    }
  }, [open, invoiceTarget?.id, statement?.id, balanceDue]);

  const amountDue = linkAmount
    ?? (statement ? statement.amountDue : balanceDue ?? (invoice.amount + (invoice.taxAmount || 0)));

  // A statement link is always for the whole balance; only an invoice takes a partial amount.
  const requestLink = async () => {
    const payload: { amount?: number } = {};
    if (!statement && balanceDue != null && balanceDue > 0) {
      payload.amount = balanceDue;
    }
    const response = await api.post(`${routeBase}/payment-link`, payload);
    setPaymentLinkUrl(response.data.url);
    if (typeof response.data.amount === 'number') setLinkAmount(response.data.amount);
  };

  const handleGenerateLink = async () => {
    setStep('generating');
    setErrorMessage('');
    try {
      await requestLink();
      setStep('ready');
    } catch (error: any) {
      setErrorMessage(
        error.response?.data?.message || 'Failed to generate payment link',
      );
      setStep('error');
    }
  };

  const handleSendEmail = async () => {
    if (!recipientEmail) {
      enqueueSnackbar('Please enter a recipient email address', { variant: 'warning' });
      return;
    }
    setStep('sending');
    setErrorMessage('');
    try {
      await api.post(
        `${routeBase}/send-payment-link-email`,
        statement ? { recipientEmail } : { recipientEmail, amount: amountDue },
      );
      setStep('sent');
      enqueueSnackbar(`Payment link sent to ${recipientEmail}`, { variant: 'success' });
      onSuccess();
    } catch (error: any) {
      setErrorMessage(
        error.response?.data?.message || 'Failed to send payment link email',
      );
      setStep('ready');
      enqueueSnackbar('Failed to send payment link email', { variant: 'error' });
    }
  };

  const handleGenerateAndSend = async () => {
    if (!recipientEmail) {
      enqueueSnackbar('Please enter a recipient email address', { variant: 'warning' });
      return;
    }
    setStep('generating');
    setErrorMessage('');
    try {
      await requestLink();
      setStep('sending');
      await api.post(
        `${routeBase}/send-payment-link-email`,
        statement
          ? { recipientEmail }
          : { recipientEmail, amount: balanceDue != null && balanceDue > 0 ? balanceDue : undefined },
      );
      setStep('sent');
      enqueueSnackbar(`Payment link sent to ${recipientEmail}`, { variant: 'success' });
      onSuccess();
    } catch (error: any) {
      const msg = error.response?.data?.message || 'Failed to send payment link';
      setErrorMessage(msg);
      setStep(paymentLinkUrl ? 'ready' : 'error');
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  const handleCopyLink = async () => {
    if (!paymentLinkUrl) return;
    try {
      await navigator.clipboard.writeText(paymentLinkUrl);
      setCopied(true);
      enqueueSnackbar('Payment link copied to clipboard', { variant: 'success' });
      setTimeout(() => setCopied(false), 3000);
    } catch {
      enqueueSnackbar('Failed to copy link', { variant: 'error' });
    }
  };

  const isLoading = step === 'generating' || step === 'sending';

  return (
    <Dialog open={open} onClose={isLoading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinkIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h6" component="span">
            Send Payment Link
          </Typography>
        </Box>
        <IconButton onClick={onClose} disabled={isLoading} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2.5 }}>
        {/* Invoice Summary */}
        <Box sx={{
          bgcolor: 'grey.50',
          borderRadius: 2,
          p: 2,
          mb: 2.5,
          border: '1px solid',
          borderColor: 'divider',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">{statement ? 'Statement' : 'Invoice'}</Typography>
            <Typography variant="body2" fontWeight="bold">
              {statement ? statement.invoiceNumber : invoice.invoiceNumber}
            </Typography>
          </Box>
          {!statement && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Patient</Typography>
                <Typography variant="body2">{invoice.horse.name}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Due Date</Typography>
                <Typography variant="body2" data-testid="payment-link-due-date">
                  {/* CALENDAR DATE (DATE-KINDS §1.1 Invoice.dueDate): a DAY the client posts as
                      yyyy-MM-dd, stored at a UTC anchor. Re-wrapping it with `new Date()` read that
                      anchor in the browser's zone and printed the PREVIOUS day in every US zone —
                      on the summary a client is asked to pay from. Parse the date part; never
                      re-zone it, not even into the clinic's zone (the INVERSE trap). */}
                  {formatCalendarDate(invoice.dueDate)}
                </Typography>
              </Box>
            </>
          )}
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary" fontWeight="bold">
              Amount Due
            </Typography>
            <Typography variant="h6" color="primary.main" fontWeight="bold">
              ${amountDue.toFixed(2)}
            </Typography>
          </Box>
        </Box>

        {/* Recipient Email */}
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.secondary' }}>
            Send to
          </Typography>
          {editingEmail ? (
            <TextField
              fullWidth
              size="small"
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              onBlur={() => setEditingEmail(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') setEditingEmail(false);
              }}
              autoFocus
              placeholder="owner@example.com"
            />
          ) : (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'background.paper',
            }}>
              <Box>
                <Typography variant="body2" fontWeight="medium" data-testid="payment-link-recipient-name">
                  {resolveRecipientName()}
                </Typography>
                <Typography variant="body2" color="text.secondary" data-testid="payment-link-recipient-email">
                  {recipientEmail || 'No email on file'}
                </Typography>
              </Box>
              <Tooltip title="Edit email">
                <IconButton size="small" onClick={() => setEditingEmail(true)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>

        {/* Status Messages */}
        {step === 'sent' && (
          <Alert
            severity="success"
            icon={<CheckCircleIcon />}
            sx={{ mb: 2 }}
          >
            Payment link sent to <strong>{recipientEmail}</strong>. The link expires in 24 hours.
          </Alert>
        )}

        {step === 'error' && errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        {step === 'ready' && paymentLinkUrl && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Payment link is active and expires in 24 hours. You can send it via email or copy the link to share through other channels.
          </Alert>
        )}

        {/* Copy Link (shown when link is generated) */}
        {paymentLinkUrl && (step === 'ready' || step === 'sent') && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1.5,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'grey.50',
          }}>
            <Typography
              variant="body2"
              sx={{
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: 'text.secondary',
                fontFamily: 'monospace',
                fontSize: '0.75rem',
              }}
            >
              {paymentLinkUrl}
            </Typography>
            <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
              <IconButton size="small" onClick={handleCopyLink}>
                {copied ? (
                  <CheckCircleIcon fontSize="small" color="success" />
                ) : (
                  <CopyIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={isLoading}>
          {step === 'sent' ? 'Done' : 'Cancel'}
        </Button>

        {step === 'initial' && (
          <>
            <Button
              variant="outlined"
              startIcon={<LinkIcon />}
              onClick={handleGenerateLink}
              disabled={isLoading}
            >
              Generate Link Only
            </Button>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={handleGenerateAndSend}
              disabled={isLoading || !recipientEmail}
            >
              Generate & Send
            </Button>
          </>
        )}

        {step === 'generating' && (
          <Button variant="contained" disabled startIcon={<CircularProgress size={18} />}>
            Generating Link...
          </Button>
        )}

        {step === 'ready' && (
          <>
            <Button
              variant="outlined"
              startIcon={copied ? <CheckCircleIcon /> : <CopyIcon />}
              onClick={handleCopyLink}
            >
              {copied ? 'Copied!' : 'Copy Link'}
            </Button>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={handleSendEmail}
              disabled={!recipientEmail}
            >
              Send via Email
            </Button>
          </>
        )}

        {step === 'sending' && (
          <Button variant="contained" disabled startIcon={<CircularProgress size={18} />}>
            Sending...
          </Button>
        )}

        {step === 'error' && (
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={handleGenerateAndSend}
            disabled={!recipientEmail}
          >
            Retry
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SendPaymentLinkDialog;
